import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '../../../app/app.config';
import {
  CreateDialogMessageModel,
  DialogMessage,
  DialogMessageMarkAsReadModel,
  DialogModel,
  DialogUnreadMessagesModel,
  DialogMessageMarkMessagesAsReadModel,
  DialogMessageAttachmentModel,
} from '../model/dialog';
import { Observable, catchError, finalize, map, of } from 'rxjs';
import { MessageService } from '../../core/service/message.service';
import { ProgressBarService } from '../../core/service/progress-bar.service';

@Injectable()
export class ChatService {
  constructor(
    @Inject(APP_CONFIG) config: IAppConfig,
    private _httpClient: HttpClient,
    private _messageService: MessageService,
    private _progressBar: ProgressBarService
  ) {
    this._config = config;
  }

  private _config: IAppConfig;

  getDialogMessages(dialogId: string, dialogEndpoint: string): Observable<DialogModel> {
    return this._httpClient.get<DialogModel>(`${this._config.apiEndpointV2}/${dialogEndpoint}/${dialogId}`).pipe(
      map(dialog => {
        dialog.dialogMessages = dialog.dialogMessages.map(item => Object.assign(new DialogMessage(), item));
        return dialog;
      }),
      catchError(error => {
        this._messageService.showError(error);
        return [];
      })
    );
  }

  getDialogMessageAttachments(messageId: string, dialogEndpoint: string): Observable<DialogMessageAttachmentModel[]> {
    return this._httpClient
      .get<
        {
          id: string;
          caption: string;
          size: number;
          extension: string;
          data: string;
        }[]
      >(`${this._config.apiEndpointV2}/${dialogEndpoint}/message/attachments/${messageId}`)
      .pipe(
        map(attachments =>
          attachments.map(x => {
            const model = new DialogMessageAttachmentModel();
            Object.assign(model, x);
            model.data = this.decodeBase64(x.data);
            return model;
          })
        ),
        catchError(error => {
          this._messageService.showError(error);
          return of([]);
        })
      );
  }

  createDialogMessage(dialogModel: CreateDialogMessageModel, dialogEndpoint: string) {
    this._progressBar.start();
    return this._httpClient
      .post<DialogModel>(`${this._config.apiEndpointV2}/${dialogEndpoint}/message`, this.createMultiPartFormData(dialogModel))
      .pipe(
        catchError(error => {
          this._messageService.showError('CHAT.MESSAGE.MESSAGE_NOT_SENT');
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  markAsRead(dialogModel: DialogMessageMarkAsReadModel, dialogEndpoint: string): Observable<DialogMessage> {
    this._progressBar.start();
    return this._httpClient.post(`${this._config.apiEndpointV2}/${dialogEndpoint}/mark-read`, JSON.stringify(dialogModel)).pipe(
      map(message => Object.assign(new DialogMessage(), message)),
      catchError(error => {
        this._messageService.showError(error);
        return [];
      }),
      finalize(() => this._progressBar.stop())
    );
  }

  markAsUnread(dialogModel: DialogMessageMarkAsReadModel, dialogEndpoint: string): Observable<DialogMessage> {
    this._progressBar.start();
    return this._httpClient.post(`${this._config.apiEndpointV2}/${dialogEndpoint}/mark-unread`, JSON.stringify(dialogModel)).pipe(
      map(message => Object.assign(new DialogMessage(), message)),
      catchError(error => {
        this._messageService.showError(error);
        return [];
      }),
      finalize(() => this._progressBar.stop())
    );
  }

  markMessagesAsRead(dialogModel: DialogMessageMarkMessagesAsReadModel, dialogEndpoint: string): Observable<DialogMessage[]> {
    return this._httpClient
      .post<
        DialogMessage[]
      >(`${this._config.apiEndpointV2}/${dialogEndpoint}/mark-read-messages`, JSON.stringify(dialogModel), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map(messages => messages.map(message => Object.assign(new DialogMessage(), message))),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        })
      );
  }

  hasUnreadMessage(dialogEndpoint: string = 'fault-notification/dialog'): Observable<DialogUnreadMessagesModel> {
    return this._httpClient.get<DialogUnreadMessagesModel>(`${this._config.apiEndpointV2}/${dialogEndpoint}/unread-messages`).pipe(
      map(response => Object.assign(new DialogUnreadMessagesModel(), response)),
      catchError(error => {
        this._messageService.showError(error);
        return of(new DialogUnreadMessagesModel());
      })
    );
  }

  deleteMessage(messageId: string, dialogEndpoint: string) {
    this._progressBar.start();
    return this._httpClient.delete(`${this._config.apiEndpointV2}/${dialogEndpoint}/message/${messageId}`).pipe(
      catchError(error => {
        this._messageService.showError(error);
        return [];
      }),
      finalize(() => this._progressBar.stop())
    );
  }

  private createMultiPartFormData(model: CreateDialogMessageModel): FormData {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model.toJSON()));
    for (let file of model.attachments) {
      formData.append('attachments[]', file, file.name);
    }
    return formData;
  }

  private decodeBase64(base64String: string): Uint8Array {
    const binaryString = window.atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }
}
